import { fetchInChunks } from '~/utils/batch';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import { uploadSessionRepository } from './uploadSessionRepository';
import type {
  CreateUnitDto,
  HydratedUnitDto,
  HydratedUnitPaginationDto,
  TransactionPaginationDto,
  UnitDto,
  UpdateUnitDto,
  UpdateUnitMagicTagDto,
} from '~/swagger/portal/Api';
import type { ApiFilter } from '~/types/apiFilters';

const UNITS_URL = '/api/units';

export type UnitFilter = ApiFilter<{
  propertyId?: string;
  portfolioId?: string;
  searchText?: string;
}>;

export type UnitPaymentFilter = ApiFilter<{
  unitId?: string;
}>;

export type UnitImagePayload = {
  file: File;
};

export class UnitRepository extends BaseRestRepository {
  public async getUnits(filter?: UnitFilter): Promise<HydratedUnitPaginationDto> {
    const url = urlWithQuery(UNITS_URL, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async addUnit(unit: CreateUnitDto): Promise<UnitDto> {
    const url = UNITS_URL;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(unit),
    });
    return this.getJsonResponse(response);
  }

  public async getUnit(unitId: string, showErrorMessage = true): Promise<HydratedUnitDto> {
    const url = `${UNITS_URL}/${unitId}`;
    const response = await this.fetchWithAuth(
      url,
      {
        method: 'GET',
      },
      showErrorMessage
    );
    return this.getJsonResponse(response);
  }

  public async batchGetUnitsByIds(unitIds: string[]): Promise<HydratedUnitDto[]> {
    return fetchInChunks(unitIds, async (idsChunk) => {
      const url = urlWithQuery(`${UNITS_URL}/batch`, { unitIds: idsChunk });
      const response = await this.fetchWithAuth(url);
      return this.getJsonResponse(response);
    });
  }

  /** Not preferred. use deactivateUnit instead in most cases */
  public async deleteUnit(unitId: string) {
    const url = `${UNITS_URL}/${unitId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
    return this.getJsonResponse(response);
  }

  public async deactivateUnit(unitId: string, forceEndLease?: boolean): Promise<void> {
    const url = urlWithQuery(`${UNITS_URL}/${unitId}/deactivate`, { forceEndLease });
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async updateUnit(unitId: string, unit: UpdateUnitDto): Promise<HydratedUnitDto> {
    const url = `${UNITS_URL}/${unitId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(unit),
    });
    return this.getJsonResponse(response);
  }

  public async addUnitImage(unitId: string, image: UnitImagePayload): Promise<UnitDto> {
    const [uploadSessionId] = await uploadSessionRepository.uploadFiles(`${UNITS_URL}/${unitId}/image/upload-sessions`, [image.file]);

    const url = `${UNITS_URL}/${unitId}/image`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ uploadSessionId }),
    });
    return this.getJsonResponse(response);
  }

  public async updateUnitMagicTags(unitId: string, magicTags: UpdateUnitMagicTagDto): Promise<UnitDto> {
    const url = `${UNITS_URL}/${unitId}/magic-tags`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(magicTags),
    });
    return this.getJsonResponse(response);
  }

  public async getUnitPayments(filter?: UnitPaymentFilter): Promise<TransactionPaginationDto> {
    const url = urlWithQuery(`${UNITS_URL}/${filter?.unitId}/payments`, filter);

    const response = await this.fetchWithAuth(url);

    return this.getJsonResponse(response);
  }
}

export const unitRepository = new UnitRepository();
